import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {StyledSpinner} from './StyledSpinner';

const Spinner = ({
  size = 'medium',
  ...rest
}) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    // `lottie-web` is 25 MB installed and 0.11 MB gzipped in the bundle, for this one animation, and
    // it was in the entry bundle — so every user downloaded a whole animation engine before the
    // first screen rendered. Loaded on demand instead, which moves it into its own chunk
    // (H1MVP-4742).
    //
    // It stays a real dependency in package.json. A dynamic import is still an import: webpack
    // resolves it at build time, so dropping it from the manifest breaks a clean install with
    // "Can't resolve 'lottie-web'" while still building fine anywhere node_modules is warm.
    //
    // The branded logo animation itself is unchanged. Swapping it for a generic spinner would alter
    // the loading state across the entire app — Spin, Table and ComponentLoading all render this —
    // which is a design decision, not a payload one.
    let anim;
    let cancelled = false;

    Promise.all([
      import('lottie-web'),
      import('../../static/images/spinner-logo.json')
    ])
      .then(([{default: lottie}, {default: animationData}]) => {
        // The imports can resolve after the spinner has already gone away, which is the common case
        // for a fast request — attaching then would leak an animation onto a detached node.
        if (cancelled || !animationContainer.current) return;
        anim = lottie.loadAnimation({
          container: animationContainer.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData
        });
      })
      // A loading indicator that fails to load must not take the page down with it.
      .catch(() => {});

    return () => {
      cancelled = true;
      anim?.destroy(); // Clean up when component unmounts
    };
  }, []);

  return (
    <StyledSpinner size={size} {...rest}>
      <div ref={animationContainer} />
    </StyledSpinner>
  );
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['xxsmall', 'xsmall', 'small', 'medium', 'large'])
};

export default Spinner;
